<template>
  <section>
    <div class="popups">
      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
      >
        <template>
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="
                    applyFilters();
                    loadCurrentView();
                    showFilters = false;
                  "
                  >Apply</b-button
                >
              </div>
              <div class="col-auto text-right">
                <b-button
                  type="reset"
                  variant="outline-danger"
                  @click="resetFormFilters()"
                >
                  Reset
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="ml-1"
                  @click="
                    showFilters = false;
                    formFilters = { ...itemsFilters };
                  "
                >
                  Cancel
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group label="Storefront:">
              <b-v-select
                v-model="formFilters.projectId"
                :searchable="true"
                :options="projectsFilter"
              />
            </b-form-group>
            <b-form-group label="SKU:">
              <b-form-input v-model="formFilters.sku" placeholder="Storefront SKU" />
            </b-form-group>
            <b-form-group label="WMS SKU:">
              <b-form-input v-model="formFilters.wmsSku" placeholder="WMS item code" />
            </b-form-group>
            <b-form-group label="Status:">
              <b-v-select
                v-model="formFilters.status"
                :options="statusOpts"
              />
            </b-form-group>
            <b-form-group label="Type">
              <b-form-select v-model="formFilters.type">
                <option value="">Show All</option>
                <option value="Kit">Bundle</option>
                <option value="Stock Item">Single Item</option>
              </b-form-select>
            </b-form-group>
          </div>
        </template>
      </b-sidebar>
    </div>

    <div class="header">
      <div class="header-body">
        <div class="row align-items-end">
          <h6 class="col-12 header-pretitle">
            Product Mappings
          </h6>
          <div class="col">
            <div class="d-flex align-items-center mb-1" style="gap: 0.5rem;">
              <b-button-group>
                <b-button variant="primary" disabled>
                  Active
                </b-button>
                <b-button
                  variant="outline-secondary"
                  :to="{ name: 'Storefronts.ProductMappingRequests.Index' }"
                  tag="router-link"
                >
                  Requests
                </b-button>
              </b-button-group>
            </div>
            <ActiveFiltersCard
              :itemsFilters="itemsFilters"
              :resetFilterWithKey="resetFilterWithKey"
              :loadItems="loadCurrentView"
            >
              <template v-slot:filterVal(projectId)="data">
                <span v-if="myProjects && myProjects.find(a => a.id == data.item)">
                  {{ myProjects.find(a => a.id == data.item).name }}
                </span>
                <span v-else>-</span>
              </template>
            </ActiveFiltersCard>
          </div>
          <div class="col-auto">
            <b-button-group class="mr-2">
              <b-button
                :variant="viewMode === 'grouped' ? 'primary' : 'outline-primary'"
                @click="setViewMode('grouped')"
              >
                Grouped By Product
              </b-button>
              <b-button
                :variant="viewMode === 'basic' ? 'secondary' : 'outline-secondary'"
                @click="setViewMode('basic')"
              >
                Basic
              </b-button>
            </b-button-group>
            <b-button class="mr-2" variant="info" @click="ExportPage">
              <i class="fa fa-cloud-download mr-2"></i>
              Export
            </b-button>
            <span
              v-if="itemsFilters.projectId"
              v-b-tooltip.hover
              title=""
              class="d-inline-block mr-2"
            >
              <input
                id="importMappingsFile"
                ref="importFile"
                type="file"
                class="d-none"
                accept=".xls,.xlsx"
                name="importMappingsFile"
                @change="ImportMappings()"
              />
              <label for="importMappingsFile" class="btn btn-secondary mb-0">
                <i class="fa fa-cloud-upload mr-2"></i>
                Import
              </label>
            </span>
            <span
              v-else
              v-b-tooltip.hover
              title="Select a storefront in Filters to enable import"
              class="d-inline-block mr-2"
            >
              <b-button variant="secondary" disabled>
                <i class="fa fa-cloud-upload mr-2"></i>
                Import
              </b-button>
            </span>
            <b-button variant="primary" @click="showFilters = !showFilters">
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Basic flat view -->
    <TableListing
      v-if="viewMode === 'basic'"
      class="mb-0"
      :loading-page="loadingItems"
      :failed-loading="failedLoadItems"
      head-variant="dark"
      :items.sync="items"
      :fields.sync="fields"
      :pagination.sync="pagination"
      :options.sync="tableOptions"
      v-on:pagination-changed="loadItems"
    >
      <template v-slot:cell(storefront)="data">
        <span v-if="myProjects && data.item.product && myProjects.find(a => a.id == data.item.product.projectId)">
          {{ myProjects.find(a => a.id == data.item.product.projectId).name }}
        </span>
        <span v-else>-</span>
      </template>
      <template v-slot:cell(sku)="data">
        <div v-if="data.item.product">{{ data.item.product.sku }}</div>
        <div v-if="data.item.product" class="text-muted border-top pt-1">{{ data.item.product.name }}</div>
      </template>
      <template v-slot:cell(wmsItem)="data">
        <span v-if="data.item.wmsProduct">{{ data.item.wmsProduct.item }}</span>
      </template>
      <template v-slot:cell(wmsDescription)="data">
        <span v-if="data.item.wmsProduct">{{ data.item.wmsProduct.description }}</span>
      </template>
      <template v-slot:cell(type)="data">
        <span v-if="data.item.type == 'Contains'" class="badge badge-info">Contains</span>
        <span v-else-if="data.item.type == 'Synonym'" class="badge badge-secondary">One-To-One</span>
        <span v-else>{{ data.item.type }}</span>
      </template>
      <template v-slot:cell(defaultSalesPrice)="data">
        <span v-if="data.item.defaultSalesPrice != null">
          ${{ parseFloat(data.item.defaultSalesPrice).toFixed(2) }}
        </span>
        <span v-else>-</span>
      </template>
      <template v-slot:cell(quantityAvail)="data">
        <span v-if="data.item.wmsProduct">{{ data.item.wmsProduct.quantityAvail }}</span>
      </template>
      <template v-slot:cell(addedByUserName)="data">
        <span v-if="data.item.addedByUserName">{{ data.item.addedByUserName }}</span>
        <span v-else class="text-muted">-</span>
      </template>
      <template v-slot:cell(reviewedByUserName)="data">
        <span v-if="data.item.reviewedByUserName">{{ data.item.reviewedByUserName }}</span>
        <span v-else class="text-muted">-</span>
      </template>
    </TableListing>

    <!-- Grouped by product view -->
    <TableListing
      v-else
      class="mb-0"
      :loading-page="groupedLoading"
      :failed-loading="groupedFailed"
      head-variant="dark"
      :items.sync="groupedItems"
      :fields.sync="groupedFields"
      :pagination.sync="groupedPagination"
      :options.sync="groupedTableOptions"
      v-on:pagination-changed="loadGroupedItems"
    >
      <template v-slot:cell(storefront)="data">
        <span v-if="myProjects && data.item.product && myProjects.find(a => a.id == data.item.product.projectId)">
          {{ myProjects.find(a => a.id == data.item.product.projectId).name }}
        </span>
        <span v-else>-</span>
      </template>
      <template v-slot:cell(sku)="data">
        <div v-if="data.item.product">{{ data.item.product.sku }}</div>
        <div v-if="data.item.product" class="text-muted border-top pt-1">{{ data.item.product.name }}</div>
      </template>
      <template v-slot:cell(mappings)="data">
        <div v-if="data.item.mappings && data.item.mappings.length > 0">
          <div
            v-for="(m, idx) in data.item.mappings"
            :key="idx"
            class="mb-1"
          >
            <span class="ml-2">
              <span v-if="m.type === 'Contains' || m.type === 0" class="badge badge-info"> × {{ m.quantity }}</span>
              <span v-else class="badge badge-secondary"> × {{ m.quantity }}</span>
            </span>
            <strong> {{ m.wmsProduct ? m.wmsProduct.item : m.wmsProductId }}</strong>
            <span v-if="m.wmsProduct && m.wmsProduct.description" class="text-muted ml-1"> - {{ m.wmsProduct.description }}</span>
            <span v-if="m.defaultSalesPrice != null" class="ml-1 text-muted small">${{ parseFloat(m.defaultSalesPrice).toFixed(2) }}</span>
          </div>
        </div>
        <span v-else class="text-muted">—</span>
      </template>
      <template v-slot:cell(id)="data">
        <b-button
          variant="info"
          class="ml-1"
          :to="{
            name: 'Storefronts.StorefrontProducts.Edit',
            params: { id: data.item.id }
          }"
        >
          <i class="fa fa-cog"></i>
        </b-button>
      </template>
    </TableListing>
  </section>
</template>

<script>
import ActiveFiltersCard from '@/views/components/ActiveFiltersCard.vue';
import TableListing from '@/views/components/TableListing.vue';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'Storefronts.ProductMappings.Index',
  mixins: [crudMixinGen('productMappings'), myCommonDataMixin],
  components: {
    TableListing,
    ActiveFiltersCard
  },

  data: () => ({
    viewMode: 'grouped',
    groupedItems: [],
    groupedLoading: false,
    groupedFailed: false,
    groupedPagination: { page: 1, perPage: 25 },
    groupedTableOptions: { totalRows: 0, totalPages: 0 },
    groupedFields: [
      { key: 'storefront', label: 'Storefront', sortable: false },
      { key: 'sku', label: 'SKU', sortable: false },
      { key: 'mappings', label: 'Mappings', sortable: false },
      { key: 'id', label: 'Actions', sortable: false }
    ],
    statusOpts: [
      { value: '', text: 'Mapped (default)' },
      { value: 'isMapped', text: 'Mapped' },
      { value: 'notMapped', text: 'Not Mapped' }
    ],
    fields: [
      { key: 'storefront', label: 'Storefront', sortable: false },
      { key: 'sku', label: 'SKU', sortable: false },
      { key: 'quantity', label: 'Qty (Mapping)', sortable: false },
      { key: 'type', label: 'Type', sortable: false },
      { key: 'wmsItem', label: 'WMS Item', sortable: false },
      { key: 'wmsDescription', label: 'WMS Description', sortable: false },
      { key: 'defaultSalesPrice', label: 'Default Price', sortable: false },
      { key: 'quantityAvail', label: 'Qty Available', sortable: false },
      { key: 'addedByUserName', label: 'Mapped By', sortable: false },
      { key: 'reviewedByUserName', label: 'Approved By', sortable: false }
    ]
  }),

  mounted() {
    this.loadGroupedItems();
  },

  methods: {
    loadCurrentView() {
      if (this.viewMode === 'grouped') {
        this.loadGroupedItems();
      } else {
        this.loadItems();
      }
    },

    setViewMode(mode) {
      this.viewMode = mode;
      if (mode === 'grouped') {
        this.loadGroupedItems();
      } else {
        this.loadItems();
      }
    },

    loadGroupedItems() {
      let $this = this;
      $this.groupedLoading = true;
      $this.groupedFailed = false;
      var filters = Object.assign({}, $this.itemsFilters, {
        page: $this.groupedPagination.page,
        perPage: $this.groupedPagination.perPage
      });
      $this.$http
        .post('/api/common/productMappings/list/grouped-by-product', filters)
        .then(function(resp) {
          if (resp.data) {
            $this.groupedItems = resp.data.items || [];
            $this.groupedTableOptions = {
              totalRows: resp.data.total || 0,
              totalPages: resp.data.totalPages || 0
            };
            $this.groupedPagination = {
              page: resp.data.page || 1,
              perPage: resp.data.perPage || 25
            };
          }
        })
        .catch(function() {
          $this.groupedFailed = true;
        })
        .finally(function() {
          $this.groupedLoading = false;
        });
    },

    ExportPage() {
      let form = document.createElement('form');
      form.setAttribute('method', 'post');
      form.setAttribute('target', '_blank');
      form.setAttribute('action', process.env.VUE_APP_API_URL + '/api/common/productMappings/download/list');
      var params = Object.assign({}, this.itemsFilters, {
        page: this.pagination.page,
        perPage: this.pagination.perPage
      });
      for (var key in params) {
        var hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
    },

    ImportMappings() {
      let $this = this;
      let filePicked = this.$refs.importFile.files[0];
      if (!filePicked) { return; }
      let formData = new FormData();
      formData.append('formFile', filePicked);
      $this.$http
        .post('/api/common/productMappings/import/' + $this.itemsFilters.projectId, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(resp => {
          if (resp.data.success) {
            $this.$swal('Success', 'Mappings imported successfully.', 'success');
            $this.loadItems();
          } else {
            $this.$swal('Error', resp.data.errorMessage || 'Import failed.', 'error');
          }
        })
        .catch(err => {
          let msg = (err.response && err.response.data && err.response.data.errorMessage) || 'An error occurred during import.';
          $this.$swal('Error', msg, 'error');
        })
        .finally(() => {
          $this.$refs.importFile.value = '';
        });
    }
  }
};
</script>
